import React, { lazy, ReactElement, Suspense, useMemo } from "react";
import ReactDOM from "react-dom/client";

import "./index.css";
import "react-toastify/dist/ReactToastify.css";

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";

// This configures the API client to use the correct base URL and hooks up
// error handling and authentication (However, the actual authentication
// configuration is done in the ApiContext compontent)
import "./api/apiConfiguration";

import { moduleType } from "regen-api-ts-client";

import { ApiContext } from "./api/apiConfiguration";
import App from "./App";
import GeneralErrorScreen from "./components/GeneralErrorScreen";
import { RedirectingAuthenticationGuard } from "./components/guards/RedirectingAuthenticationGuard";
import { RedirectingStaffGuard } from "./components/guards/RedirectingStaffGuard";
import { Loading } from "./components/Loading";
import FarmDataProvider, { farmDataLoader } from "./pages/DataProvider";
import Layout from "./pages/layouts/Layout";
import Logout from "./pages/Logout";
import { LocalStorageService } from "./services/LocalStorage";
import { ONBOARDING_ROUTES } from "./util/constants";

const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const Map = lazy(() => import("./pages/Map"));
const InteractiveMapPage = lazy(() => import("./pages/InteractiveMapPage"));
const FarmDetails = lazy(() => import("./pages/FarmDetails"));
const UserProfileInternal = lazy(
  () => import("./pages/Admin/UserProfileInternal"),
);
const UserProfile = lazy(() => import("./pages/UserProfile/UserProfile"));
const FarmList = lazy(() => import("./pages/Admin/FarmList"));
const UserList = lazy(() => import("./pages/Admin/UserList"));
const SystemContent = lazy(() => import("./pages/Admin/SystemContent"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AdminMap = lazy(() => import("./pages/Admin/Map"));
const FarmInternals = lazy(
  () => import("./pages/Admin/FarmInternals/FarmInternals"),
);
const InternalFarmInfo = lazy(
  () => import("./pages/Admin/FarmInternals/InternalFarmInfo"),
);
const InternalFieldList = lazy(
  () => import("./pages/Admin/FarmInternals/InternalFieldList"),
);
const InternalFieldEdit = lazy(
  () => import("./pages/Admin/FarmInternals/InternalFieldEdit"),
);
const InternalRecommendations = lazy(
  () => import("./pages/Admin/FarmInternals/InternalRecommendations"),
);
const InternalModuleEdit = lazy(
  () => import("./pages/Admin/FarmInternals/InternalModuleEdit"),
);
const InternalImagery = lazy(
  () => import("./pages/Admin/FarmInternals/InternalImagery"),
);
const InternalImageryDownload = lazy(
  () => import("./pages/Admin/FarmInternals/InternalImageryDownload"),
);
const FarmAttachments = lazy(
  () => import("./pages/Admin/FarmInternals/FarmAttachments/FarmAttachments"),
);
const OnboardingRegisterFarm = lazy(
  () => import("./pages/Onboarding/RegisterFarm"),
);
const FieldBoundaries = lazy(
  () => import("./pages/Onboarding/FieldBoundariesPage"),
);
const OnboardingFarmDetails = lazy(
  () => import("./pages/Onboarding/FarmDetailsPage"),
);
const VerifyEmail = lazy(() => import("./pages/Onboarding/VerifyEmail"));
const OnboardingComplete = lazy(
  () => import("./pages/Onboarding/OnboardingComplete"),
);
const CoverCrops = lazy(() => import("./pages/Modules/CoverCropsPage"));
const Tillage = lazy(() => import("./pages/Modules/TillagePage"));
const NutrientManagement = lazy(
  () => import("./pages/Modules/NutrientManagementPage"),
);
const SoilHealth = lazy(() => import("./pages/Modules/SoilHealthPage"));
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});
const enableReactQueryDevtools =
  import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === "true";

const moduleComponents: { [key in moduleType]?: ReactElement } = {
  [moduleType.FIELD_PROPERTIES]: <InteractiveMapPage />,
  [moduleType.COVER_CROPS]: <CoverCrops />,
  [moduleType.TILLAGE]: <Tillage />,
  [moduleType.NUTRIENT_MANAGEMENT]: <NutrientManagement />,
  [moduleType.SOIL_HEALTH]: <SoilHealth />,
};

const getModulePage = (moduleId: moduleType, showModulePage = true) => {
  return {
    path: `${moduleId}/:annum`,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Loading />}>
            {showModulePage ? (
              moduleComponents[moduleId]
            ) : (
              <GeneralErrorScreen />
            )}
          </Suspense>
        ),
      },
      {
        path: "field/:fieldId",
        element: (
          <Suspense fallback={<Loading />}>
            {showModulePage ? (
              moduleComponents[moduleId]
            ) : (
              <GeneralErrorScreen />
            )}
          </Suspense>
        ),
      },
    ],
  };
};

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const RouterProviderWithContext = () => {
  // Call auth0 hook here to pass data to loaders. Loaders fire before auth guards so we need
  // to explicitly check authentication in them before performing any data fetches.
  const authData = useAuth0();

  const router = useMemo(
    () =>
      sentryCreateBrowserRouter(
        [
          {
            path: "/",
            // App does nothing other than render an <Outlet /> and determine where
            // to forward the user (to onboarding, to their dashboard, to admin pages)
            element: <RedirectingAuthenticationGuard Component={App} />,
            errorElement: <GeneralErrorScreen />,
            children: [
              {
                element: <Layout />,
                children: [
                  {
                    path: "farm/:farmId",
                    element: (
                      <Suspense fallback={<Loading />}>
                        <FarmDataProvider />
                      </Suspense>
                    ),
                    loader: farmDataLoader(queryClient, authData),
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<Loading />}>
                            <Dashboard />
                          </Suspense>
                        ),
                      },
                      {
                        path: ":annum?",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <Dashboard />
                          </Suspense>
                        ),
                      },
                      {
                        path: "map/:annum?",
                        children: [
                          {
                            index: true,
                            element: (
                              <Suspense fallback={<Loading />}>
                                <InteractiveMapPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "field/:fieldId/:datasourceId?",
                            element: (
                              <Suspense fallback={<Loading />}>
                                <InteractiveMapPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "field/:fieldId/:datasourceId/:layerId?",
                            element: (
                              <Suspense fallback={<Loading />}>
                                <InteractiveMapPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "field/:fieldId/:datasourceId/:layerId/:layerDate?",
                            element: (
                              <Suspense fallback={<Loading />}>
                                <InteractiveMapPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: "field-map/:annum?",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <Map />
                          </Suspense>
                        ),
                      },
                      {
                        path: "farm-details",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <FarmDetails />
                          </Suspense>
                        ),
                      },
                      getModulePage(moduleType.FIELD_PROPERTIES),
                      getModulePage(moduleType.COVER_CROPS),
                      getModulePage(moduleType.TILLAGE),
                      getModulePage(moduleType.NUTRIENT_MANAGEMENT),
                      getModulePage(moduleType.SOIL_HEALTH),
                    ],
                  },
                ],
              },
              {
                path: "admin",
                element: <RedirectingStaffGuard element={<Outlet />} />,
                children: [
                  {
                    element: <Layout />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<Loading />}>
                            <RedirectingStaffGuard element={<FarmList />} />
                          </Suspense>
                        ),
                      },
                      {
                        path: "map",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <RedirectingStaffGuard element={<AdminMap />} />
                          </Suspense>
                        ),
                      },
                      {
                        path: "users",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <RedirectingStaffGuard element={<UserList />} />
                          </Suspense>
                        ),
                      },
                      {
                        path: "system-content/:annum?",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <RedirectingStaffGuard
                              element={<SystemContent />}
                            />
                          </Suspense>
                        ),
                      },
                      {
                        path: "farm/:farmId",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <FarmDataProvider />
                          </Suspense>
                        ),
                        loader: farmDataLoader(queryClient, authData),
                        children: [
                          {
                            path: "",
                            element: (
                              <Suspense fallback={<Loading />}>
                                <RedirectingStaffGuard
                                  element={<FarmInternals />}
                                />
                              </Suspense>
                            ),
                            children: [
                              {
                                // The ? means that this will also load for
                                // /admin/farm/:farmId/ and /admin/farm/:farmId/info
                                // making it the default tab
                                path: "info?",
                                element: <InternalFarmInfo />,
                                // handle data is included in the matches list
                                // returned by useMatches, allowing us to pass arbitrary
                                // data from the router configuration to the component.
                                handle: { farmInternalTab: "info" },
                              },
                              {
                                path: "fields/:annum?",
                                handle: { farmInternalTab: "fields" },
                                children: [
                                  {
                                    index: true,
                                    element: (
                                      <Suspense fallback={<Loading />}>
                                        <InternalFieldList />
                                      </Suspense>
                                    ),
                                  },
                                  {
                                    path: "field/:fieldId",
                                    element: <InternalFieldEdit />,
                                  },
                                ],
                              },
                              {
                                path: "recommendations",
                                element: <InternalRecommendations />,
                                handle: { farmInternalTab: "recommendations" },
                                children: [
                                  {
                                    index: true,
                                    element: <Loading />,
                                  },
                                  {
                                    path: ":moduleId/:annum/field/:fieldId",
                                    element: <InternalModuleEdit />,
                                  },
                                  {
                                    path: ":moduleId/:annum",
                                    element: <InternalModuleEdit />,
                                  },
                                ],
                              },
                              {
                                path: "attachments",
                                element: <FarmAttachments />,
                                handle: { farmInternalTab: "attachments" },
                              },
                              {
                                path: "imagery",
                                element: <InternalImagery />,
                                handle: { farmInternalTab: "imagery" },
                                children: [
                                  {
                                    index: true,
                                    element: <Loading />,
                                  },
                                  {
                                    path: ":annum/field/:fieldId/:layer?",
                                    element: <InternalImageryDownload />,
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: "user/:userId",
                        element: (
                          <Suspense fallback={<Loading />}>
                            <RedirectingStaffGuard
                              element={<UserProfileInternal />}
                            />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: "onboarding",
                element: <Layout />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Loading />}>
                        <OnboardingRegisterFarm />
                      </Suspense>
                    ),
                  },
                  {
                    path: ONBOARDING_ROUTES.WELCOME,
                    element: (
                      <Suspense fallback={<Loading />}>
                        <OnboardingRegisterFarm />
                      </Suspense>
                    ),
                  },
                  {
                    path: ONBOARDING_ROUTES.FIELD_BOUNDARIES,
                    element: (
                      <Suspense fallback={<Loading />}>
                        <FieldBoundaries />
                      </Suspense>
                    ),
                  },
                  {
                    path: ONBOARDING_ROUTES.FARM_DETAILS,
                    element: (
                      <Suspense fallback={<Loading />}>
                        <OnboardingFarmDetails />
                      </Suspense>
                    ),
                  },
                  {
                    path: ONBOARDING_ROUTES.VERIFY_EMAIL,
                    element: (
                      <Suspense fallback={<Loading />}>
                        <VerifyEmail />
                      </Suspense>
                    ),
                  },
                  {
                    path: ONBOARDING_ROUTES.COMPLETE,
                    element: (
                      <Suspense fallback={<Loading />}>
                        <OnboardingComplete />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                element: <Layout />,
                children: [
                  {
                    path: "user-profile",
                    element: (
                      <Suspense fallback={<Loading />}>
                        <UserProfile />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "auth/callback",
                element: (
                  <Suspense fallback={<Loading />}>
                    <AuthCallback />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "logout",
            element: <Logout />,
          },
        ],
        {
          future: {
            v7_relativeSplatPath: true,
          },
        },
      ),
    [authData],
  );
  {
    /* The various router children underneath Layout which is lazy loaded
     * may load before it and so this seemingly weird Suspense wrapping
     * the RouterProvider is one solution given for the error that happens
     * in that case.
     * https://github.com/TanStack/router/issues/857#issuecomment-2100990754
     */
  }
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <RouterProvider router={router} />
    </Suspense>
  );
};

// Check the url for a url auth token and set that in local storage.
// Once the token is used and a 403 is returned, the token will be removed
// from local storage and regular auth0 flow will be used.
// This is only allowed for specific paths, and the token can only be used
// on the same path prefix.
// Note - this only needs to happen once on startup, as the link will be from
// an external source, not navigated to from within the page.
// At the moment, we only generate paths for nutrient management and tillage
const allowableAuthOverridePaths = RegExp(
  `(/farm/[a-zA-Z0-9-]*/(${moduleType.NUTRIENT_MANAGEMENT}|${moduleType.TILLAGE})/).*/`,
);
const searchParams = new URLSearchParams(window?.location.search);
const overridePathMatch = allowableAuthOverridePaths.exec(
  window?.location.pathname,
);
// Find out if there is an `_auth` query parameter and the url matches a
// valid auth override path.
if (searchParams.has("_auth") && overridePathMatch) {
  const token = searchParams.get("_auth");
  const url = overridePathMatch[1];
  token &&
    LocalStorageService.setItem(
      LocalStorageService.Keys.AUTH_OVERRIDE,
      JSON.stringify({ token, url }),
    );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      cacheLocation="memory"
      authorizationParams={{
        redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
      }}
    >
      <ApiContext />
      <ToastContainer />
      <QueryClientProvider client={queryClient}>
        {enableReactQueryDevtools && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
        <RouterProviderWithContext />
      </QueryClientProvider>
    </Auth0Provider>
  </React.StrictMode>,
);
